import {
  Button,
  createStyles,
  Grid,
  Paper,
  Typography,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import { Show } from "features/shared/components";
import React, { useState } from "react";
import {
  createLotGoal,
  deleteLotGoal,
  reactivateLotGoal,
  updateLotGoal,
} from "redux/operations";
import { Goal } from "shared/types";
import { useFilterForm } from "../hooks/useFilterForm";
import { LotGoalFormState } from "../lotGoal.types";
import LotGoalFilterForm from "./lotGoalFilterForm";
import LotGoalForm from "./lotGoalForm";
import LotGoalListTable from "./lotGoalListTable";

type LotGoalPageProps = {
  activateGoal: (
    ...params: Parameters<typeof reactivateLotGoal>
  ) => Promise<void>;
  createGoal: (...params: Parameters<typeof createLotGoal>) => Promise<void>;
  deleteGoal: (...params: Parameters<typeof deleteLotGoal>) => Promise<void>;
  goals: Goal[];
  updateGoal: (...params: Parameters<typeof updateLotGoal>) => Promise<void>;
} & WithStyles<typeof styles>;

function LotGoalPage({
  activateGoal,
  classes,
  createGoal,
  deleteGoal,
  goals,
  updateGoal,
}: LotGoalPageProps) {
  const { t } = useTranslations();

  const [isFormPanelOpen, setIsFormPanelOpen] = useState<boolean>(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  const { filterForm, handleChangeFilter } = useFilterForm();

  const toggleFormPanel = (isOpen: boolean) => {
    setIsFormPanelOpen(isOpen);
    if (isOpen) {
      return;
    }
    setSelectedGoal(null);
  };

  const handleCloseFormPanel = () => {
    toggleFormPanel(false);
  };

  const handleOpenNewGoalPanel = () => {
    setSelectedGoal(null);
    toggleFormPanel(true);
  };

  const handleSubmitForm = async (formState: LotGoalFormState) => {
    if (selectedGoal != null) {
      await updateGoal(selectedGoal.id, {
        name: formState.name.value,
      });
      toggleFormPanel(false);
      return;
    }

    await createGoal({ name: formState.name.value });

    toggleFormPanel(false);
  };

  const handleEditGoal = (row: Goal) => {
    setSelectedGoal(row);
    toggleFormPanel(true);
  };

  const handleToggleStatus = (row: Goal, isCurrentDeleted: boolean) => {
    if (isCurrentDeleted) {
      activateGoal(row.id);
      return;
    }

    deleteGoal(row.id);
  };

  return (
    <Paper className={classes.root} square={true}>
      <Grid item sm={12} xs={12} className={classes.grid}>
        <Typography className={classes.header} gutterBottom={true}>
          {t("livestock:goal-title")}
        </Typography>
        <LotGoalFilterForm
          filterForm={filterForm}
          onChangeFilter={handleChangeFilter}
        />

        <Show
          when={isFormPanelOpen}
          fallback={
            <AddNewButton classes={classes} onClick={handleOpenNewGoalPanel} />
          }
        >
          <LotGoalForm
            goals={goals}
            onCancel={handleCloseFormPanel}
            onSubmit={handleSubmitForm}
            selectedGoal={selectedGoal}
          />
        </Show>

        <LotGoalListTable
          goals={goals}
          filterForm={filterForm}
          isFormPanelOpen={isFormPanelOpen}
          onEditClick={handleEditGoal}
          onToggleStatus={handleToggleStatus}
        />
      </Grid>
    </Paper>
  );
}

type AddNewButtonProps = {
  onClick: () => void;
} & WithStyles<typeof styles>;

function AddNewButton({ classes, onClick }: AddNewButtonProps) {
  const { t } = useTranslations();

  return (
    <Button
      className={classes.addNewButton}
      color="primary"
      data-testid="add-new-goal-button"
      onClick={onClick}
      variant="contained"
    >
      {t("livestock:new-goal-button")}
    </Button>
  );
}

const styles = () =>
  createStyles({
    root: {
      bottom: 0,
      height: "100%",
      minHeight: "calc(100vh - 90px)",
      overflowX: "auto",
      padding: "46px",
      top: "17%",
      width: "100%",
      zIndex: -1,
    },
    grid: {
      backgroundColor: "#fff",
      flexGrow: 1,
      height: "100%",
      width: "100%",
    },
    header: {
      fontSize: "20px",
      fontWeight: "bold",
      fontStyle: "medium",
      fontStretch: "medium",
      lineHeight: "medium",
      letterSpacing: "medium",
      color: "#333333",
      marginTop: "40px",
    },
    addNewButton: {
      borderRadius: "18px",
      float: "right",
      marginBottom: "16px",
      marginRight: 0,
      marginTop: "34px",
      width: "153px",
    },
  });

export default withStyles(styles)(LotGoalPage);
